import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";
import { sanitizeEmail } from "../utils/sanitization";
import { notify } from "../components/UI/DesignStandards/Notifications/notify";
import {
  uiSpans,
  captureException,
  captureMessage,
} from "../utils/sentryUtils";

export const useSignInForm = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [success, setSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [signInMethod, setSignInMethod] = useState("password"); // "password" or "otp"
  const [showOTPEmailInput, setShowOTPEmailInput] = useState(false); // Show email input for OTP flow
  const [otpCodeSent, setOtpCodeSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpError, setOtpError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  // Try to get the intended destination from multiple sources
  const getIntendedDestination = () => {
    // First priority: location state from ProtectedRoute
    if (location.state?.from) {
      const fromPath = location.state.from;
      // Validate it's a same-site path (starts with /)
      if (fromPath && typeof fromPath === 'string' && fromPath.startsWith('/')) {
        return fromPath;
      }
    }

    // Second priority: check session storage for original destination
    const storedDestination = sessionStorage.getItem("signInDestination");
    if (storedDestination && storedDestination !== "/signin") {
      // Validate it's a same-site path (starts with /)
      if (storedDestination.startsWith('/')) {
        return storedDestination;
      }
    }

    // Third priority: check if we came from a different page
    const referrer = document.referrer;
    if (referrer && referrer !== window.location.href) {
      try {
        const referrerUrl = new URL(referrer);
        const referrerPath = referrerUrl.pathname;
        // Only use referrer if it's not the signin page and it's our domain
        if (
          referrerPath !== "/signin" &&
          referrerUrl.hostname === window.location.hostname
        ) {
          return referrerPath;
        }
      } catch (e) {
        // Invalid URL, ignore
      }
    }

    // Default: go to home
    return "/";
  };

  const intendedDestination = getIntendedDestination();

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    if (name === "email") {
      sanitizedValue = sanitizeEmail(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
    setErrors((prev) => ({
      ...prev,
      submit: "Captcha verification failed. Please try again.",
    }));
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken("");
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    return uiSpans.formSubmit("Sign In", async () => {
      e.preventDefault();
      setLoading(true);
      setErrors({});

      if (!captchaToken) {
        setErrors({
          submit: "Please complete the captcha verification",
        });
        setLoading(false);
        return;
      }

      try {
        // Use AuthContext.signIn - no error means success
        await signIn(formData.email, formData.password, captchaToken);

        // If we get here, sign-in was successful (no error thrown)
        setSuccess(true);

        // Clear the saved destination
        sessionStorage.removeItem("signInDestination");

        // Log successful sign-in to Sentry
        captureMessage("User successfully signed in", "info", {
          email: sanitizeEmail(formData.email),
          intendedDestination,
          retryCount,
        });

        // Check if we should redirect or stay on current page
        if (intendedDestination && intendedDestination !== location.pathname) {
          // User was redirected from a protected route, go back there
          setTimeout(() => {
            navigate(intendedDestination);
          }, 1500);
        } else {
          // User signed in from a public route, stay on current page
          // Show success message briefly, then reset form and unlock
          setTimeout(() => {
            setSuccess(false);
            setLoading(false);
            resetForm();
          }, 2000);
        }
      } catch (err) {
        console.error("Sign in error:", err);

        // Capture the error with Sentry
        captureException(err, {
          tags: {
            action: "sign_in",
            component: "SignInForm",
            retryCount: retryCount + 1,
          },
          extra: {
            email: sanitizeEmail(formData.email),
            hasCaptchaToken: !!captchaToken,
            intendedDestination,
          },
        });

        setErrors({
          submit: err.message || "Unable to sign in. Try again.",
        });

        setRetryCount((prev) => prev + 1);
        setLoading(false);
      }
    });
  };

  const resetForm = () => {
    setFormData({ email: "", password: "" });
    setErrors({});
    setCaptchaToken("");
    setSuccess(false);
    setRetryCount(0);
    setSignInMethod("password");
    setShowOTPEmailInput(false);
    setOtpCodeSent(false);
    setOtpCode("");
    setOtpError(null);
  };

  // Start OTP flow - show email input
  const handleStartOTPFlow = () => {
    setSignInMethod("otp");
    setShowOTPEmailInput(true);
    setOtpError(null);
  };

  // Send OTP code for sign-in
  const handleSendOTP = async () => {
    if (!formData.email) {
      setErrors({ email: "Email is required" });
      return;
    }

    setSendingOTP(true);
    setOtpError(null);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: formData.email,
        options: {
          shouldCreateUser: false, // Don't create user, just send OTP
        },
      });

      if (otpError) {
        throw otpError;
      }

      setOtpCodeSent(true);
      notify.success("Verification code sent to your email");
    } catch (error) {
      console.error("Error sending OTP:", error);
      const errorMessage =
        error?.message || "Failed to send verification code. Please try again.";

      // Check for rate limiting errors
      if (
        error?.status === 429 ||
        errorMessage.toLowerCase().includes("rate limit") ||
        errorMessage.toLowerCase().includes("too many requests") ||
        errorMessage.toLowerCase().includes("you can only request this after")
      ) {
        const waitTimeMatch = errorMessage.match(/after (\d+) seconds?/i);
        if (waitTimeMatch) {
          const seconds = parseInt(waitTimeMatch[1], 10);
          setOtpError(
            `Please wait ${seconds} seconds before requesting another verification code.`
          );
        } else {
          setOtpError(
            "Too many requests. Please wait a moment before requesting another verification code."
          );
        }
      } else {
        setOtpError(errorMessage);
      }
    } finally {
      setSendingOTP(false);
    }
  };

  // Verify OTP code and sign in
  const handleVerifyOTP = async (code) => {
    setOtpCode(code);
    setOtpError(null);

    // Validate format: 6 digits
    if (!/^\d{6}$/.test(code)) {
      return;
    }

    setLoading(true);

    try {
      const { data: verifyData, error: verifyError } =
        await supabase.auth.verifyOtp({
          email: formData.email,
          token: code,
          type: "email",
        });

      if (verifyError || !verifyData) {
        const errorMsg =
          verifyError?.message || "Invalid or expired verification code";
        setOtpError(errorMsg);
        setLoading(false);
        return;
      }

      // OTP verified successfully - user is now signed in
      // The auth state change will be handled by AuthContext
      setSuccess(true);

      // Clear the saved destination
      sessionStorage.removeItem("signInDestination");

      // Log successful sign-in to Sentry
      captureMessage("User successfully signed in with OTP", "info", {
        email: sanitizeEmail(formData.email),
        intendedDestination,
        retryCount,
      });

      // Check if we should redirect or stay on current page
      if (intendedDestination && intendedDestination !== location.pathname) {
        setTimeout(() => {
          navigate(intendedDestination);
        }, 1500);
      } else {
        setTimeout(() => {
          setSuccess(false);
          setLoading(false);
          resetForm();
        }, 2000);
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError("Failed to verify code. Please try again.");
      setLoading(false);
    }
  };

  // Resend OTP code
  const handleResendOTP = async () => {
    setOtpCode("");
    setOtpError(null);
    await handleSendOTP();
  };

  // Switch back to password sign-in
  const handleBackToPassword = () => {
    setSignInMethod("password");
    setShowOTPEmailInput(false);
    setOtpCodeSent(false);
    setOtpCode("");
    setOtpError(null);
  };

  return {
    formData,
    errors,
    loading,
    captchaToken,
    success,
    retryCount,
    signInMethod,
    showOTPEmailInput,
    otpCodeSent,
    otpCode,
    sendingOTP,
    otpError,
    handleChange,
    handleSubmit,
    handleCaptchaError,
    handleCaptchaExpire,
    handleCaptchaVerify,
    resetForm,
    handleStartOTPFlow,
    handleSendOTP,
    handleVerifyOTP,
    handleResendOTP,
    handleBackToPassword,
  };
};
